/**
 * Brand Portal Context
 * Manages brand authentication state and provides auth methods
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BrandContext = createContext();

// Use environment variable or default based on environment
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    // Use the Heroku backend URL in production
    return 'https://yemberzal-app-28481d1d0b39.herokuapp.com';
  }
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();


export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Clear session function
  const clearSession = () => {
    localStorage.removeItem('brand_portal_brand');
    localStorage.removeItem('brand_portal_token');
    localStorage.removeItem('brand_portal_refresh_token');
    localStorage.removeItem('brand_portal_token_expiry');
    setBrand(null);
    setAccessToken(null);
  };

  // Refresh access token function
  const refreshAccessToken = useCallback(async (refreshToken) => {
    try {
      const response = await fetch(`${API_URL}/api/brand/refresh-token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        const newAccessToken = data.access_token;
        const newExpiry = new Date().getTime() + (60 * 60 * 1000); // 1 hour

        localStorage.setItem('brand_portal_token', newAccessToken);
        localStorage.setItem('brand_portal_token_expiry', newExpiry.toString());
        setAccessToken(newAccessToken);
        return true;
      } else {
        clearSession();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearSession();
      return false;
    }
  }, [API_URL]);

  // Check for existing session on mount
  useEffect(() => {
    const storedBrand = localStorage.getItem('brand_portal_brand');
    const storedToken = localStorage.getItem('brand_portal_token');
    const tokenExpiry = localStorage.getItem('brand_portal_token_expiry');

    if (storedBrand && storedToken && tokenExpiry) {
      // Check if token is still valid
      if (new Date().getTime() < parseInt(tokenExpiry)) {
        setBrand(JSON.parse(storedBrand));
        setAccessToken(storedToken);
      } else {
        // Token expired, try to refresh
        const refreshToken = localStorage.getItem('brand_portal_refresh_token');
        if (refreshToken) {
          refreshAccessToken(refreshToken);
        } else {
          clearSession();
        }
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const loginUrl = `${API_URL}/api/brand/login/`;
    
    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        return { 
          success: false, 
          error: `Server error (${response.status}). Please check if the API server is running at ${API_URL}` 
        };
      }

      const data = await response.json();

      if (response.ok && data.success) {
        // Store session
        setBrand(data.brand);
        setAccessToken(data.access_token);
        
        localStorage.setItem('brand_portal_brand', JSON.stringify(data.brand));
        localStorage.setItem('brand_portal_token', data.access_token);
        localStorage.setItem('brand_portal_refresh_token', data.refresh_token);
        localStorage.setItem('brand_portal_token_expiry', (data.expires_at * 1000).toString());

        return { success: true };
      } else {
        return { 
          success: false, 
          error: data.error || 'Login failed',
          deactivated: data.deactivated || false
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return { 
          success: false, 
          error: `Cannot connect to server at ${API_URL}. Please ensure the backend server is running.` 
        };
      }
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    clearSession();
  };

  // Helper function to make authenticated API calls
  const authFetch = async (endpoint, options = {}) => {
    const headers = {
      ...options.headers,
    };

    // Only set JSON content type when we're not sending FormData
    const isFormData = options.body instanceof FormData;
    const hasContentTypeHeader =
      headers['Content-Type'] || headers['content-type'];

    if (!isFormData && !hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // If unauthorized, try to refresh token
    if (response.status === 401) {
      const refreshToken = localStorage.getItem('brand_portal_refresh_token');
      if (refreshToken) {
        const refreshed = await refreshAccessToken(refreshToken);
        if (refreshed) {
          // Retry the request with new token
          headers['Authorization'] = `Bearer ${localStorage.getItem('brand_portal_token')}`;
          return fetch(`${API_URL}${endpoint}`, { ...options, headers });
        }
      }
    }

    return response;
  };

  const value = {
    brand,
    accessToken,
    loading,
    isAuthenticated: !!brand && !!accessToken,
    login,
    logout,
    authFetch,
    refreshAccessToken,
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}

export default BrandContext;
